import React, { FC, useState } from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import { renderRichText } from "gatsby-source-contentful/rich-text"

import { ImageType } from "@srcTypes"
import { TestimonialBlockFragmentFragment } from "@graphqlTypes"

import { IconButton, ICON_TYPES } from "@helpscout/hs-marketing-shared-components"

import AnchorLink from "@components/AnchorLink/AnchorLink"
import AvatarWithName from "@components/AvatarWithName/AvatarWithName"
import TextLink, { TEXT_LINK_SIZES } from "@components/TextLink/TextLink"
import { AVATAR_SIZES } from "@components/Avatar/Avatar"

import "./TestimonialBlockCarousel.scss"

const TestimonialBlockCarousel: FC<{
  hideAvatars: TestimonialBlockFragmentFragment["hideAvatars"],
  items: TestimonialBlockFragmentFragment["items"]
}> = ({
  hideAvatars,
  items,
}) => {
    if (!items) return null

    const [currentItemIndex, setCurrentItemIndex] = useState<number>(0)

    return (
      <div className="TestimonialBlockCarousel">
        <div className="carousel-wrapper">
          <div className="container">
            <div
              className="TestimonialBlockCarousel--Cards"
              style={{
                transform: `translateX(calc((var(--testimonial-card-width) + var(--spacing--M) / 2) * -${currentItemIndex}))`
              }}
            >
              {items.map(item => {
                if (!item) return null

                const wrapperProps = {
                  className: "TestimonialBlockCarousel--Card TestimonialCard",
                  key: item.id,
                }

                const _children = (
                  <>
                    <div className="quote">
                      {item.title && <h4 className="type--headline-medium-XL">{item.title}</h4>}
                      {item.quote?.raw && renderRichText({ raw: item.quote.raw, references: [] })}
                    </div>
                    <footer>
                      <div className="attribution">
                        {item.person && (
                          <AvatarWithName
                            name={item.person.name || ""}
                            bio={item.person.jobTitle || ""}
                            image={hideAvatars ? undefined : (item.person.image as ImageType)}
                            size={AVATAR_SIZES.S}
                          />
                        )}
                        {item.companyLogo && (item.companyLogo.logo?.imgixImage?.gatsbyImageData ? (
                          <GatsbyImage
                            alt={item.companyLogo.companyName || "Company Logo"}
                            className="logo"
                            image={item.companyLogo.logo?.imgixImage?.gatsbyImageData}
                          />
                        ) : (
                          // SVG support
                          <img
                            alt={item.companyLogo.companyName || "Company Logo"}
                            className="logo"
                            src={item.companyLogo.logo?.imgixImage?.url || ""}
                          />
                        ))}
                      </div>
                      {item.url && (
                        <div className="read-story">
                          <TextLink size={TEXT_LINK_SIZES.S}>Read More</TextLink>
                        </div>
                      )}
                    </footer>
                  </>
                )
                if (!item.url) return <div {...wrapperProps}>{_children}</div>
                return <AnchorLink {...wrapperProps} href={item.url}>{_children}</AnchorLink>
              })}
            </div>
          </div>
        </div>
        <div className="container buttons">
          <IconButton
            aria-label="View previous testimonials"
            disabled={currentItemIndex === 0}
            onClick={() => setCurrentItemIndex(_index => _index - 1)}
            iconType={ICON_TYPES["arrow-left"]}
          />
          <IconButton
            aria-label="View next testimonials"
            disabled={currentItemIndex === items.length - 1}
            onClick={() => setCurrentItemIndex(_index => _index + 1)}
            iconType={ICON_TYPES["arrow-right"]}
          />
        </div>
      </div>
    )
  }

export default TestimonialBlockCarousel
